// src/utils/comparisonUtils.js
// Shared utility functions for comparison analytics and cashflow display

import { isTextMetric, NEW_TRANCHE_LABEL } from './analyticsRows';
import { columnHasData, formatCouponPercent, monthlyDateSortKey } from './cashflowDisplay';

// Metrics where a higher value is generally "unfavorable" (higher = more risk/cost)
export const UNFAVORABLE_HIGHER = new Set([
  'WAL', 'Mod_Duration_BP', 'Eff_Duration_BP', 'MOD_Convexity_BP',
  'Mod_Duration_MP', 'Eff_Duration_MP', 'Mod_Convexity_MP',
]);

// Metrics where a higher value is generally "favorable" (higher = better return)
export const FAVORABLE_HIGHER = new Set([
  'Book_Yield', 'Market_Yield', 'Spread_BP', 'Spread_MP',
  'Z_spread_BP', 'Z_spread_MP', 'DISC_MARGIN_BP', 'DISC_MARGIN_MP',
  'Book_Price', 'Market_Price', 'BOOK_VALUE', 'PV',
]);

export const getDeltaCellStyle = (metricName, deltaValue) => {
  if (deltaValue === null || deltaValue === undefined || isNaN(deltaValue) || deltaValue === 0) {
    return {};
  }
  const isPositive = deltaValue > 0;

  // For known metrics, use favorable/unfavorable semantics
  // For unknown metrics, just use green=positive, red=negative
  let isGreen;
  if (FAVORABLE_HIGHER.has(metricName)) {
    isGreen = isPositive;
  } else if (UNFAVORABLE_HIGHER.has(metricName)) {
    isGreen = !isPositive;
  } else {
    isGreen = isPositive;
  }

  return {
    backgroundColor: isGreen ? 'rgba(46, 125, 50, 0.15)' : 'rgba(198, 40, 40, 0.15)',
    color: isGreen ? '#2e7d32' : '#c62828',
    fontWeight: 600,
  };
};

const rowTranche = (row) => row?.TRANCHE || row?.Tranche || row?.tranche || '';
const rowCusip = (row) => row?.CUSIP || row?.Cusip || row?.cusip || '';

// Extract all tranche names from analytics data across all scenarios
export const extractTranches = (scenarios) => {
  const trancheSet = new Set();
  Object.values(scenarios).forEach(scenData => {
    const analytics = scenData?.analytics;
    if (Array.isArray(analytics) && analytics.length > 0) {
      const firstRow = analytics[0];
      Object.keys(firstRow || {}).forEach(key => {
        const match = key.match(/^Tranche\s+(.+?):/i);
        if (match) trancheSet.add(match[1]);
      });
    }

    const cashflow = scenData?.cashflow;
    if (Array.isArray(cashflow)) {
      cashflow.forEach((row) => {
        const tranche = rowTranche(row);
        if (tranche) trancheSet.add(tranche);
      });
    }
  });
  return Array.from(trancheSet);
};

export const extractCashflowCusips = (scenarios, selectedTranche) => {
  const cusips = new Set();
  Object.values(scenarios || {}).forEach((scenario) => {
    if (!Array.isArray(scenario?.cashflow)) return;
    scenario.cashflow.forEach((row) => {
      const cusip = rowCusip(row);
      if (rowTranche(row) === selectedTranche && cusip) cusips.add(cusip);
    });
  });
  return Array.from(cusips);
};

const valueFormatter = ({ value }) => {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'number') {
    if (isNaN(value)) return '--';
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }
  // Text metrics (principal window, price source) are shown as-is.
  return value;
};

const deltaValueFormatter = ({ value }) => {
  if (value === null || value === undefined || isNaN(value)) return '--';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
};

const cashflowValueFormatter = ({ value }) =>
  value != null ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '--';

// Build analytics column definitions for comparison grid. `newTranches` names
// the tranches we hold nothing of; each gets a marker on its half of the header.
export const buildAnalyticsColDefs = (scenarioLabels, allTranches, baseLabel, newTranches) => {
  if (scenarioLabels.length === 0) return [];

  const newTrancheSet = newTranches instanceof Set ? newTranches : new Set(newTranches || []);
  const trancheLabel = (tranche) =>
    (newTrancheSet.has(tranche) ? `${tranche} · ${NEW_TRANCHE_LABEL}` : tranche);

  const cols = [
    {
      headerName: 'Metric',
      field: 'Metric',
      pinned: 'left',
      width: 200,
      sortable: true,
      filter: true,
      cellStyle: { fontWeight: 'bold' },
    },
  ];

  // Keep comparison headers flat. AG Grid grouped headers can retain stale
  // ProvidedColumnGroup references when React remounts a tab/result panel,
  // which crashes inside HeaderGroupCellCtrl. Explicit labels preserve the
  // scenario/tranche hierarchy without entering that lifecycle path.
  scenarioLabels.forEach(label => {
    allTranches.forEach(tranche => {
      cols.push({
        headerName: `${label} · ${trancheLabel(tranche)}`,
        field: `${label}__${tranche}`,
        width: 150,
        sortable: true,
        valueFormatter,
      });
    });
  });

  // Add delta columns (Run N - Base) for each non-base scenario
  if (baseLabel && scenarioLabels.includes(baseLabel)) {
    const nonBaseLabels = scenarioLabels.filter(l => l !== baseLabel);
    nonBaseLabels.forEach(label => {
      allTranches.forEach(tranche => {
        cols.push({
          headerName: `Delta (${label} - ${baseLabel}) · ${trancheLabel(tranche)}`,
          field: `delta__${label}__${tranche}`,
          width: 190,
          sortable: true,
          valueFormatter: deltaValueFormatter,
          cellStyle: (params) => {
            const metricName = params.data?.Metric || '';
            return getDeltaCellStyle(metricName, params.value);
          },
        });
      });
    });
  }

  return cols;
};

// Build analytics row data with delta computation
export const buildAnalyticsRowData = (scenarios, scenarioLabels, allTranches, baseLabel) => {
  if (scenarioLabels.length === 0 || allTranches.length === 0) return [];

  const firstScenWithData = scenarioLabels.find(label => {
    const analytics = scenarios[label]?.analytics;
    return Array.isArray(analytics) && analytics.length > 0;
  });
  if (!firstScenWithData) return [];

  const firstAnalytics = scenarios[firstScenWithData].analytics;
  const metrics = firstAnalytics.map(row => row.Metric).filter(Boolean);

  return metrics.map(metric => {
    const row = { Metric: metric };

    scenarioLabels.forEach(label => {
      const analytics = scenarios[label]?.analytics;
      if (!Array.isArray(analytics)) return;
      const metricRow = analytics.find(r => r.Metric === metric);
      if (!metricRow) return;

      allTranches.forEach(tranche => {
        const matchingKey = Object.keys(metricRow).find(
          k => k.startsWith(`Tranche ${tranche}:`)
        );
        const value = matchingKey ? metricRow[matchingKey] : null;
        // Numbers only, except the metrics whose values are text by design.
        const keep = typeof value === 'number' || (typeof value === 'string' && isTextMetric(metric));
        row[`${label}__${tranche}`] = keep ? value : null;
      });
    });

    // Compute deltas relative to base
    if (baseLabel && scenarioLabels.includes(baseLabel)) {
      const nonBaseLabels = scenarioLabels.filter(l => l !== baseLabel);
      nonBaseLabels.forEach(label => {
        allTranches.forEach(tranche => {
          const current = row[`${label}__${tranche}`];
          const base = row[`${baseLabel}__${tranche}`];
          if (typeof current === 'number' && typeof base === 'number') {
            row[`delta__${label}__${tranche}`] = current - base;
          } else {
            row[`delta__${label}__${tranche}`] = null;
          }
        });
      });
    }

    return row;
  });
};

// Writedown/interest shortfall/prepay penalty are genuinely zero for most
// bonds we hold - a column only earns its place when columnHasData finds a
// real value among the rows actually displayed (see getGatedCashflowFields).
// COUPON is not on this list: it is always present, never gated.
export const GATED_CASHFLOW_FIELDS = [
  ['WRITEDOWN', 'Writedown'],
  ['INTEREST_SHORTFALL', 'Interest Shortfall'],
  ['PREPAY_PENALTY', 'Prepay Penalty'],
];

// Build cashflow column definitions for comparison grid. `grain` picks the
// pinned period column (YEAR vs DATE); `gatedFields` is the subset of
// GATED_CASHFLOW_FIELDS (from getGatedCashflowFields) worth showing for the
// rows currently on screen. Both default so a caller that only passes
// scenarioLabels still gets a working yearly grid.
export const buildCashflowColDefs = (scenarioLabels, grain = 'year', gatedFields = []) => {
  if (scenarioLabels.length === 0) return [];

  const isMonthly = grain === 'month';
  const cols = [
    isMonthly
      ? { headerName: 'Date', field: 'DATE', pinned: 'left', width: 100, sortable: true }
      : { headerName: 'Year', field: 'YEAR', pinned: 'left', width: 80, sortable: true },
  ];

  const cashflowFields = [
    ['PRINCIPAL', 'Principal', 150],
    ['INTEREST', 'Interest', 150],
    ['CASHFLOW', 'Total Cashflow', 170],
    ['BALANCE', 'Balance', 150],
    ...gatedFields.map(([field, displayName]) => [field, displayName, 150]),
  ];

  scenarioLabels.forEach(label => {
    cashflowFields.forEach(([field, displayName, width]) => {
      cols.push({
        headerName: `${label} · ${displayName}`,
        field: `${label}__${field}`,
        width,
        valueFormatter: cashflowValueFormatter,
      });
    });
    cols.push({
      headerName: `${label} · Coupon`,
      field: `${label}__COUPON`,
      width: 110,
      // A rate, not a dollar amount - percent formatting, never gated.
      valueFormatter: ({ value }) => (value == null ? '--' : formatCouponPercent(value)),
    });
  });

  return cols;
};

// Cashflow comparison mode: one cashflow field at a time across the runs, with each run's
// change from the base run colored like the metrics deltas. The gated fields
// (getGatedCashflowFields) follow these when the rows on screen have them.
export const CASHFLOW_COMPARE_FIELDS = [
  ['PRINCIPAL', 'Principal'],
  ['INTEREST', 'Interest'],
  ['CASHFLOW', 'Total Cashflow'],
  ['BALANCE', 'Balance'],
  ['COUPON', 'Coupon'],
];

// Cashflow fields where more is worse: a larger loss colors red.
const UNFAVORABLE_CASHFLOW_HIGHER = new Set(['WRITEDOWN', 'INTEREST_SHORTFALL']);

const signed = (text, value) => `${value > 0 ? '+' : ''}${text}`;

export const buildCashflowDeltaColDefs = (scenarioLabels, baseLabel, field, fieldName = field, grain = 'year') => {
  if (scenarioLabels.length === 0) return [];
  // Coupon is a rate, so its values and changes read in percent; the rest are dollars.
  const isCoupon = field === 'COUPON';
  const format = (value) => (isCoupon
    ? formatCouponPercent(value)
    : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const cols = [
    grain === 'month'
      ? { headerName: 'Date', field: 'DATE', pinned: 'left', width: 100, sortable: true }
      : { headerName: 'Year', field: 'YEAR', pinned: 'left', width: 80, sortable: true },
  ];
  // Headers name the field, so the comparison reads the same exported as on screen.
  scenarioLabels.forEach((label) => {
    cols.push({
      headerName: `${label} · ${fieldName}`,
      field: `${label}__${field}`,
      width: 150,
      valueFormatter: ({ value }) => (value == null ? '--' : format(value)),
    });
  });
  scenarioLabels.filter((label) => label !== baseLabel).forEach((label) => {
    cols.push({
      headerName: `Delta ${fieldName} (${label} - ${baseLabel})`,
      field: `delta__${label}`,
      width: 190,
      valueFormatter: ({ value }) => (value == null || Number.isNaN(value) ? '--' : signed(format(value), value)),
      // getDeltaCellStyle greens a rise; flip the sign it sees for a field where a rise is worse.
      cellStyle: (params) => getDeltaCellStyle(
        field, UNFAVORABLE_CASHFLOW_HIGHER.has(field) && typeof params.value === 'number' ? -params.value : params.value,
      ),
    });
  });
  return cols;
};

// Each run's change from the base run in `field`, per period row of buildCashflowRowData /
// buildCashflowMonthlyRowData.
export const addCashflowDeltas = (rows, scenarioLabels, baseLabel, field) => rows.map((row) => {
  const next = { ...row };
  scenarioLabels.filter((label) => label !== baseLabel).forEach((label) => {
    const current = row[`${label}__${field}`];
    const base = row[`${baseLabel}__${field}`];
    next[`delta__${label}`] = typeof current === 'number' && typeof base === 'number' ? current - base : null;
  });
  return next;
});

// Rows as a column layout shows them: one key per column, named by its header, so an
// export holds exactly the grid's columns.
export const rowsByColumnHeader = (rows, colDefs) => rows.map((row) => Object.fromEntries(
  colDefs.map((col) => [col.headerName, row[col.field] ?? null]),
));

// Rows for one tranche (+ optional CUSIP) from a chosen grain frame
// ('cashflow' or 'cashflowMonthly'), across every scenario. Used only to
// decide which gated columns are worth showing (getGatedCashflowFields) -
// the actual table/chart rows still come from buildCashflowRowData /
// buildCashflowMonthlyRowData.
export const filterCashflowRows = (scenarios, scenarioLabels, selectedTranche, selectedCusip, sourceField) => {
  const rows = [];
  (scenarioLabels || []).forEach(label => {
    const data = scenarios?.[label]?.[sourceField];
    if (!Array.isArray(data)) return;
    data.forEach(row => {
      if (rowTranche(row) === selectedTranche && (!selectedCusip || rowCusip(row) === selectedCusip)) {
        rows.push(row);
      }
    });
  });
  return rows;
};

// Which of GATED_CASHFLOW_FIELDS have at least one non-null, non-zero value
// among `rows` - reuses the same columnHasData gate as the single-scenario
// cashflow table, so a Non-QM comparison (all three genuinely zero) doesn't
// get three empty extra columns.
export const getGatedCashflowFields = (rows) =>
  GATED_CASHFLOW_FIELDS.filter(([field]) => columnHasData(rows, field));

// Dollar fields that sum across matching rows (e.g. multiple CUSIPs in one
// tranche/period). COUPON is handled separately below - it is a rate, not a
// dollar amount, and must never be summed.
const CASHFLOW_SUM_FIELDS = [
  'PRINCIPAL', 'INTEREST', 'CASHFLOW', 'BALANCE',
  'WRITEDOWN', 'INTEREST_SHORTFALL', 'PREPAY_PENALTY',
];

// Collapses the coupon accumulators into one rate for a period: balance
// weighted where any balance is known, a plain mean when every contributing
// balance is zero (a tranche paid down to nothing), null when no row carried a
// coupon at all -- so "not computed" stays distinct from a computed zero.
const resolveCoupon = (aggregate) => {
  if (!aggregate || !aggregate.__couponCount) return null;
  if (aggregate.__couponWeight > 0) return aggregate.__couponWeighted / aggregate.__couponWeight;
  return aggregate.__couponSum / aggregate.__couponCount;
};

// Shared pivot behind buildCashflowRowData (yearly) and
// buildCashflowMonthlyRowData (monthly): one row per period, with
// `${label}__FIELD` columns per scenario. `sourceField` picks the raw frame
// (cashflow vs cashflowMonthly), `periodField` the grouping key (YEAR vs
// DATE), and `sortComparator` orders the periods.
const buildCashflowRowsByPeriod = (
  scenarios,
  scenarioLabels,
  selectedTranche,
  selectedCusip,
  { sourceField, periodField, sortComparator }
) => {
  if (scenarioLabels.length === 0 || !selectedTranche) return [];

  const periodSet = new Set();
  const dataByScenPeriod = {};

  scenarioLabels.forEach(label => {
    const cfData = scenarios[label]?.[sourceField];
    if (!Array.isArray(cfData)) return;

    const filtered = cfData.filter(row => (
      rowTranche(row) === selectedTranche
      && (!selectedCusip || rowCusip(row) === selectedCusip)
    ));

    filtered.forEach(row => {
      const period = row[periodField];
      if (period == null) return;
      periodSet.add(period);

      const key = `${label}__${period}`;
      const aggregate = dataByScenPeriod[key] || {};
      CASHFLOW_SUM_FIELDS.forEach((field) => {
        const rawValue = row[field];
        if (rawValue === null || rawValue === undefined || rawValue === '') return;
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue)) return;
        aggregate[field] = (aggregate[field] || 0) + numericValue;
      });
      // COUPON is a rate, so it is neither summed nor taken from whichever
      // row happened to land last. Two CUSIPs in one tranche can carry
      // different coupons, and an arbitrary pick would misreport the tranche.
      // Balance-weight them, matching how the backend weights a year's
      // periods. Accumulated here and resolved once the period is complete.
      if (row.COUPON !== null && row.COUPON !== undefined && row.COUPON !== '') {
        const numericCoupon = Number(row.COUPON);
        if (Number.isFinite(numericCoupon)) {
          const balance = Number(row.BALANCE);
          const weight = Number.isFinite(balance) ? Math.abs(balance) : 0;
          aggregate.__couponWeighted = (aggregate.__couponWeighted || 0) + numericCoupon * weight;
          aggregate.__couponWeight = (aggregate.__couponWeight || 0) + weight;
          aggregate.__couponSum = (aggregate.__couponSum || 0) + numericCoupon;
          aggregate.__couponCount = (aggregate.__couponCount || 0) + 1;
        }
      }
      dataByScenPeriod[key] = aggregate;
    });
  });

  const periods = Array.from(periodSet).sort(sortComparator);

  return periods.map(period => {
    const row = { [periodField]: period };
    scenarioLabels.forEach(label => {
      const cfRow = dataByScenPeriod[`${label}__${period}`];
      CASHFLOW_SUM_FIELDS.forEach((field) => {
        row[`${label}__${field}`] = cfRow?.[field] ?? null;
      });
      row[`${label}__COUPON`] = resolveCoupon(cfRow);
    });
    return row;
  });
};

// Build cashflow row data for a selected tranche (one row per YEAR).
export const buildCashflowRowData = (
  scenarios,
  scenarioLabels,
  selectedTranche,
  selectedCusip = ''
) => buildCashflowRowsByPeriod(scenarios, scenarioLabels, selectedTranche, selectedCusip, {
  sourceField: 'cashflow',
  periodField: 'YEAR',
  sortComparator: (a, b) => a - b,
});

// Build cashflow row data at monthly grain (one row per cashflowMonthly
// period). See monthlyDateSortKey for why the sort can't be lexical/numeric
// on the raw 'MM/DD/YYYY' string.
export const buildCashflowMonthlyRowData = (
  scenarios,
  scenarioLabels,
  selectedTranche,
  selectedCusip = ''
) => buildCashflowRowsByPeriod(scenarios, scenarioLabels, selectedTranche, selectedCusip, {
  sourceField: 'cashflowMonthly',
  periodField: 'DATE',
  sortComparator: (a, b) => monthlyDateSortKey(a) - monthlyDateSortKey(b),
});

// Shared CSV download for grids and charts. Quotes strings containing commas
// and leaves numbers as-is, so spreadsheets open the file cleanly.
export const exportRowsToCsv = (rows, filenamePrefix) => {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  // A header can be a run label, which may hold a comma; quote it like a value.
  const csvRows = [
    headers.map((header) => (header.includes(',') ? `"${header}"` : header)).join(','),
    ...rows.map(row =>
      headers.map(header => {
        const val = row[header];
        if (val === null || val === undefined) return '';
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
      }).join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filenamePrefix}_${Date.now()}.csv`;
  link.click();
};
